import { EventType, InteractionType } from '@azure/msal-browser';
import { useLayoutEffect } from 'react';
/**
 * Adds a callback to the MSAL instance which sets the active account when a login
 * successfully completes, and handle acquireTokenRedirect failures.
 *
 * `acquireTokenRequest` is the request that will be used to refetch a token when
 * token acquisition fails.
 *
 * If an `acquireTokenRedirect` call is made but the user doesn't have access to
 * the application, the first time B2C returns an error saying that the user doesn't
 * have access. We trap that error and fire off a second call the `acquireTokenRedirect`,
 * which causes B2C to ask the user to log in again. When the second call returns the
 * application will be able to continue with whatever it was doing.
 */

export function useConfigurationCallback(acquireTokenRequest, instance) {
  /**
   * This must be a layout effect, so that our callback gets added before some others.
   * Otherwise the other callbacks will try to find an active account and fail, causing
   * a second login and a page refresh.
   */
  useLayoutEffect(() => {
    var callbackId = instance.addEventCallback(event => {
      if ((event.eventType === EventType.LOGIN_SUCCESS || event.eventType === EventType.ACQUIRE_TOKEN_SUCCESS) && event.interactionType === InteractionType.Redirect) {
        var _payload$account;

        var payload = event.payload;
        var localAccountId = (_payload$account = payload.account) === null || _payload$account === void 0 ? void 0 : _payload$account.localAccountId;

        if (localAccountId) {
          var account = instance.getAccountByLocalId(localAccountId);
          instance.setActiveAccount(account);
        }
      } else if (event.eventType === EventType.ACQUIRE_TOKEN_FAILURE && event.interactionType === InteractionType.Redirect) {
        var authError = event.error;

        if (authError.errorCode === 'server_error' && authError.errorMessage.includes('You do not have access')) {
          // if it fails to get a token because we don't have access, we can request a token
          // a second time, and this time it'll ask the user to log in instead.
          console.debug('requestTokenRedirect failed - retrying');
          instance.acquireTokenRedirect(acquireTokenRequest);
        }
      }
    });
    console.debug("useConfigurationCallback - Registered event callback with id: ".concat(callbackId));
    return () => {
      if (callbackId) {
        console.debug("useConfigurationCallback - Removing event callback with id: ".concat(callbackId));
        instance.removeEventCallback(callbackId);
      }
    };
  }, [instance, acquireTokenRequest]);
}